import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import api, {
  expedientesAPI, 
  partesAPI, 
  abonosAPI,
  costosExpedienteAPI,
  archivosAPI,
  notasAPI,
  levantamientoAPI,
  formatDate,
  formatFechaLimite,
  formatTime,
  formatCurrency, 
  getPorcentajeClass,
  copyToClipboard 
} from '../services/api';
import { fechaLimiteDesdeInput, fechaLimiteConHoraDesdeInput, horaLimiteDefaultParaFecha, horaLimiteDesdeIso } from '../utils/fechaCalendario';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import socketService from '../services/socketService';
import { config } from '../config';
import useIsMobile from '../hooks/useIsMobile';
import ParteForm from './ParteForm';
import AbonoForm from './AbonoForm';
import CostoExtraForm from './CostoExtraForm';
import ArchivoForm from './ArchivoForm';
import VersionControl from './VersionControl';
import FolderExplorer from './FolderExplorer.js';
import NotaForm from './NotaForm';
import NotasList from './NotasList';
import VisitaAsistenciaEditor, {
  etiquetaAsistencia,
  VisitaAsistenciaResumenNota,
} from './VisitaAsistenciaEditor';
import { generarDireccionCompleta } from '../utils/expedienteDireccion';
import { tipoProyectoFromApi, TIPO_PROYECTO_OPTIONS } from '../utils/expedienteCatalogo';

const obtenerFechaPeruHoy = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Lima'
}).format(new Date());

// Función para determinar si un archivo es visualizable en el navegador
const isVisualizableFile = (filename) => {
  const extension = filename.split('.').pop()?.toLowerCase();
  const visualizableExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'txt', 'html', 'htm', 'css', 'js', 'json', 'xml'];
  return visualizableExtensions.includes(extension);
};

/** Dirección a mostrar: campos estructurados → direccionCompleta → legacy → cliente. */
const resolverDireccionExpediente = (expediente) => {
  if (!expediente) return null;
  const trim = (v) => (v != null && String(v).trim()) || '';

  const generada = generarDireccionCompleta({
    manzana: expediente.manzana,
    lote: expediente.lote,
    sector: expediente.sector,
    distrito: expediente.distrito,
    departamento: expediente.departamento,
  });
  if (generada) {
    return { texto: generada, esFallbackCliente: false };
  }

  const direccionCompleta = trim(expediente.direccionCompleta);
  if (direccionCompleta) {
    return { texto: direccionCompleta, esFallbackCliente: false };
  }
  const partes = [];
  const lugar = trim(expediente.lugar);
  const manzana = trim(expediente.manzana);
  const lote = trim(expediente.lote);
  const calle = trim(expediente.calle);
  if (lugar) partes.push(lugar);
  if (manzana) partes.push(`MZ ${manzana}`);
  if (lote) partes.push(`LOTE ${lote}`);
  if (calle) partes.push(calle);
  if (partes.length > 0) {
    return { texto: partes.join(', '), esFallbackCliente: false };
  }
  const direccionCliente = trim(expediente.cliente?.direccion);
  if (direccionCliente) {
    return { texto: direccionCliente, esFallbackCliente: true };
  }
  return null;
};

const etiquetaTipoProyecto = (expediente) => {
  if (!expediente?.tipoProyecto) return null;
  const raw = tipoProyectoFromApi(expediente.tipoProyecto);
  if (raw === 'OTROS' && expediente.tipoProyectoOtro) {
    return expediente.tipoProyectoOtro;
  }
  const opt = TIPO_PROYECTO_OPTIONS.find((o) => o.value === raw);
  return opt?.label || raw;
};

const formatMzLote = (manzana, lote) => {
  const mz = manzana != null ? String(manzana).trim() : '';
  const lt = lote != null ? String(lote).trim() : '';
  if (mz && lt) return `MZ. ${mz.toUpperCase()} LOTE ${lt.toUpperCase()}`;
  if (mz) return `MZ. ${mz.toUpperCase()}`;
  if (lt) return `LOTE ${lt.toUpperCase()}`;
  return null;
};

const ExpedienteDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const { showSuccess, showError, showConfirm } = useToast();
  const isMobile = useIsMobile();
  const [expediente, setExpediente] = useState(null);
  const [partes, setPartes] = useState([]);
  const [abonos, setAbonos] = useState([]);
  const [archivos, setArchivos] = useState([]);
  const [notas, setNotas] = useState([]);
  const [levantamiento, setLevantamiento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('general');

  // Verificar si el usuario es PERSONAL (solo vista)
  const isPersonalUser = user?.rol === 'PERSONAL';

  // Estados para formularios
  const [showParteForm, setShowParteForm] = useState(false);
  const [showAbonoForm, setShowAbonoForm] = useState(false);
  const [showCostoExtraForm, setShowCostoExtraForm] = useState(false);
  const [editingCostoExtra, setEditingCostoExtra] = useState(null);
  const [showArchivoForm, setShowArchivoForm] = useState(false);
  const [showVersionControl, setShowVersionControl] = useState(false);
  const [selectedArchivoId, setSelectedArchivoId] = useState(null);
  const [showAsignarModal, setShowAsignarModal] = useState(false);
  const [selectedParteId, setSelectedParteId] = useState(null);
  const [personalList, setPersonalList] = useState([]);
  const [showConfirmacionModal, setShowConfirmacionModal] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [fechaLimitePersonalizada, setFechaLimitePersonalizada] = useState('');
  const [horaLimitePersonalizada, setHoraLimitePersonalizada] = useState('18:00');
  const [generandoBoleta, setGenerandoBoleta] = useState(false);
  const [fechaEmisionBoleta, setFechaEmisionBoleta] = useState(obtenerFechaPeruHoy());
  const [showNotaForm, setShowNotaForm] = useState(false);
  const [subiendoLevantamiento, setSubiendoLevantamiento] = useState(false);
  const [visitasExpediente, setVisitasExpediente] = useState([]);
  const [mostrarTodasVisitas, setMostrarTodasVisitas] = useState(false);
  const [visitaSeleccionadaId, setVisitaSeleccionadaId] = useState(null);
  const [notaSeleccionadaId, setNotaSeleccionadaId] = useState(null);
  const [generandoContrato, setGenerandoContrato] = useState(false);

  // Detectar si es expediente de tipo subdivision
  const esSubdivision = expediente?.tipo?.nombre?.toLowerCase().includes('subdivision');
  const direccionExpediente = useMemo(
    () => resolverDireccionExpediente(expediente),
    [expediente]
  );
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialFolderHint = query.get('parte') || '';

  useEffect(() => {
    const tab = query.get('tab');
    if (tab && ['general', 'partes', 'abonos', 'archivos', 'notas'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [query]);

  // Función para convertir fecha a formato español
  const formatearFechaEspanol = (fecha) => {
    if (!fecha) return null;
    
    try {
      const fechaObj = new Date(fecha);
      if (isNaN(fechaObj.getTime())) return null;
      
      const meses = [
        'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
        'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
      ];
      
      const dia = fechaObj.getUTCDate();
      const mes = meses[fechaObj.getUTCMonth()];
      const año = fechaObj.getUTCFullYear();
      
      // Formato: "15 de noviembre de 2025" (primera letra en mayúscula)
      return `${dia} de ${mes} de ${año}`;
    } catch (error) {
      console.error('Error al formatear fecha:', error);
      return null;
    }
  };

  // Función para redirigir a Programa de Subdivisiones
  const handleIrAPrograma = () => {
    if (!expediente) {
      console.error('❌ Expediente no disponible');
      showError('No se pudo obtener la información del expediente');
      return;
    }

    if (!expediente.cliente) {
      console.error('❌ Cliente no disponible en el expediente');
      showError('No se pudo obtener la información del cliente');
      return;
    }

    console.log('🔍 DEBUG: Expediente completo:', expediente);
    console.log('🔍 DEBUG: Cliente:', expediente.cliente);
    console.log('🔍 DEBUG: Campos de dirección disponibles:');
    console.log('   - calle:', expediente.calle, typeof expediente.calle);
    console.log('   - manzana:', expediente.manzana, typeof expediente.manzana);
    console.log('   - lote:', expediente.lote, typeof expediente.lote);
    console.log('   - lugar:', expediente.lugar, typeof expediente.lugar);
    console.log('   - direccionCompleta:', expediente.direccionCompleta, typeof expediente.direccionCompleta);
    console.log('   - fechaLimite:', expediente.fechaLimite, typeof expediente.fechaLimite);

    // Convertir fecha límite a formato español
    const fechaFormateada = formatearFechaEspanol(expediente.fechaLimite);
    console.log('   - fechaLimite formateada:', fechaFormateada);

    // Preparar datos para precargar en el formulario externo
    // IMPORTANTE: Solo enviar valores que no sean null/undefined/vacío
    const datosParaPrecargar = {
      cliente: {
        nombre: expediente.cliente?.nombre || '',
        dni: expediente.cliente?.dni || ''
      },
      direccion: {}
    };

    // Agregar fecha si está disponible
    if (fechaFormateada) {
      datosParaPrecargar.fecha = fechaFormateada;
    }

    // Solo agregar campos de dirección si tienen valor (no null, no undefined, no vacío)
    if (expediente.calle && expediente.calle.trim() !== '') {
      datosParaPrecargar.direccion.calle = expediente.calle.trim();
    }
    if (expediente.manzana && expediente.manzana.trim() !== '') {
      datosParaPrecargar.direccion.manzana = expediente.manzana.trim();
    }
    if (expediente.lote && expediente.lote.trim() !== '') {
      datosParaPrecargar.direccion.lote = expediente.lote.trim();
    }
    if (expediente.lugar && expediente.lugar.trim() !== '') {
      datosParaPrecargar.direccion.lugar = expediente.lugar.trim();
    }
    if (expediente.direccionCompleta && expediente.direccionCompleta.trim() !== '') {
      datosParaPrecargar.direccion.direccionCompleta = expediente.direccionCompleta.trim();
    } else {
      const dirResuelta = resolverDireccionExpediente(expediente);
      if (dirResuelta?.texto && Object.keys(datosParaPrecargar.direccion).length === 0) {
        datosParaPrecargar.direccion.direccionCompleta = dirResuelta.texto;
      }
    }

    // Si direccion está vacío, eliminarlo del objeto (no enviar objeto vacío)
    if (Object.keys(datosParaPrecargar.direccion).length === 0) {
      delete datosParaPrecargar.direccion;
    }

    console.log('📤 DEBUG: Datos preparados para enviar:');
    console.log(JSON.stringify(datosParaPrecargar, null, 2));

    // Validar que al menos tenemos nombre y DNI del cliente
    if (!datosParaPrecargar.cliente.nombre || !datosParaPrecargar.cliente.dni) {
      console.error('❌ Faltan datos esenciales del cliente');
      showError('No se pueden enviar los datos: faltan nombre o DNI del cliente');
      return;
    }

    // Codificar datos en JSON para URL
    try {
      const datosCodificados = encodeURIComponent(JSON.stringify(datosParaPrecargar));
      const urlDestino = `${config.subdivisionWebUrl}?datos=${datosCodificados}`;

      console.log('🔗 DEBUG: URL generada:');
      console.log(urlDestino);
      console.log('📏 DEBUG: Longitud de URL:', urlDestino.length);

      // Verificar decodificación
      try {
        const datosDecodificados = JSON.parse(decodeURIComponent(datosCodificados));
        console.log('✅ DEBUG: Verificación - Datos decodificados correctamente:');
        console.log(JSON.stringify(datosDecodificados, null, 2));
        
        // Mostrar alerta con la URL para copiar si es necesario
        console.log('\n📋 URL completa para copiar:');
        console.log(urlDestino);
      } catch (error) {
        console.error('❌ DEBUG: Error al decodificar:', error);
        showError('Error al codificar los datos para enviar');
        return;
      }

      // Abrir en nueva pestaña
      window.open(urlDestino, '_blank');
      showSuccess('Redirigiendo a Programa de Subdivisiones...');
    } catch (error) {
      console.error('❌ Error al generar URL:', error);
      showError('Error al generar la URL de redirección');
    }
  };

  const esPlanosConstruccion = expediente?.tipo?.nombre?.toUpperCase().includes('PLANO DE CONSTRUCCION');

  const handleGenerarContratoConstruccion = async () => {
    try {
      const estado = await expedientesAPI.getContratoConstruccion(id);
      const faltantes = estado.data?.faltantes || [];
      if (faltantes.length) {
        showError('Faltan datos del contrato: ' + faltantes.map((item) => item.label).join(', '));
        return;
      }

      const version = estado.data?.datos?.version || 0;
      if (version > 0) {
        const confirmado = await showConfirm('El contrato ya tiene una versión ' + version + '. ¿Deseas generar una nueva versión?');
        if (!confirmado) return;
      }

      setGenerandoContrato(true);
      const response = await expedientesAPI.generarContratoConstruccion(id, version > 0);
      if (response.data?.ok) {
        showSuccess('Contrato generado: ' + response.data.filename);
        await loadExpedienteData();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message || 'Error al generar el contrato';
      showError(errorMsg);
    } finally {
      setGenerandoContrato(false);
    }
  };

  // 🆕 Método para generar boleta final
  const handleGenerarBoletaFinal = async () => {
    try {
      const confirmado = await showConfirm(
        'Generar Boleta Final?\n\nSe generara la boleta final para el expediente ' + expediente.numero + '. Esta accion no se puede deshacer.'
      );

      if (!confirmado) return;

      setGenerandoBoleta(true);

      const response = await expedientesAPI.generarBoletaFinal(id, {
        metodoPago: 'TRANSFERENCIA',
        incluirIGV: false,
        fechaEmision: fechaEmisionBoleta
      });

      if (response.data.success) {
        showSuccess('Boleta final generada exitosamente');
        await loadExpedienteData(); // Recargar datos
      }

    } catch (error) {
      console.error('Error al generar boleta:', error);
      const errorMsg = error.response?.data?.error || 'Error al generar la boleta';
      showError(errorMsg);
    } finally {
      setGenerandoBoleta(false);
    }
  };

  // Cargar datos del expediente
  const loadExpedienteData = useCallback(async () => {
    try {
      setLoading(true);
      const [expedienteResponse, partesResponse, abonosResponse, archivosResponse, notasResponse, levantamientoResponse] = await Promise.all([
        expedientesAPI.getExpediente(id),
        partesAPI.getPartes(id),
        abonosAPI.getAbonos(id),
        archivosAPI.getArchivos(id),
        notasAPI.getNotas(id).catch(() => ({ data: [] })),
        levantamientoAPI.getLevantamiento(id).catch(() => ({ data: { existe: false } }))
      ]);

      setExpediente(expedienteResponse.data);
      setPartes(partesResponse.data);
      setAbonos(abonosResponse.data);
      setArchivos(archivosResponse.data);
      setNotas(notasResponse.data || []);
      setLevantamiento(levantamientoResponse.data?.existe ? levantamientoResponse.data.levantamiento : null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadExpedienteData();
    
    // Unirse al expediente para recibir actualizaciones en tiempo real
    if (id) {
      socketService.joinExpediente(id);
    }
    
    // Cleanup: salir del expediente cuando se desmonte el componente
    return () => {
      if (id) {
        socketService.leaveExpediente(id);
      }
    };
  }, [id, loadExpedienteData]);

  useEffect(() => {
    if (activeTab !== 'notas' || !id) return;
    let cancel = false;
    void (async () => {
      try {
        const { data } = await api.get('/visitas', { params: { expedienteId: id } });
        if (!cancel) setVisitasExpediente(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Error cargando visitas del expediente:', e);
        if (!cancel) setVisitasExpediente([]);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [activeTab, id]);

  useEffect(() => {
    if (!visitasExpediente.length) {
      setVisitaSeleccionadaId(null);
      return;
    }
    if (!visitaSeleccionadaId || !visitasExpediente.some((v) => v.id === visitaSeleccionadaId)) {
      setVisitaSeleccionadaId(visitasExpediente[0].id);
    }
  }, [visitasExpediente, visitaSeleccionadaId]);

  useEffect(() => {
    if (!notas.length) {
      setNotaSeleccionadaId(null);
      return;
    }
    if (!notaSeleccionadaId || !notas.some((n) => n.id === notaSeleccionadaId)) {
      setNotaSeleccionadaId(notas[0].id);
    }
  }, [notas, notaSeleccionadaId]);

  // Escuchar eventos de parte completada/actualizada para recargar datos
  useEffect(() => {
    const handleParteCompletada = (event) => {
      const { expedienteId } = event.detail;
      if (expedienteId && expedienteId.toString() === id) {
        loadExpedienteData();
      }
    };

    const handleParteActualizada = (event) => {
      const { expedienteId } = event.detail;
      if (expedienteId && expedienteId.toString() === id) {
        loadExpedienteData();
      }
    };

    window.addEventListener('parteCompletada', handleParteCompletada);
    window.addEventListener('parteActualizada', handleParteActualizada);

    return () => {
      window.removeEventListener('parteCompletada', handleParteCompletada);
      window.removeEventListener('parteActualizada', handleParteActualizada);
    };
  }, [id, loadExpedienteData]);

  // Escuchar actualizaciones del expediente en tiempo real
  useEffect(() => {
    const handleExpedienteUpdate = (update) => {
      console.log('🔄 Actualización de expediente recibida:', update);
      // Recargar datos del expediente cuando hay actualizaciones
      loadExpedienteData();
    };

    socketService.on('expediente_update', handleExpedienteUpdate);

    return () => {
      socketService.off('expediente_update', handleExpedienteUpdate);
    };
  }, [loadExpedienteData]);

  // Manejar copia de celular
  const handleCopyCelular = async () => {
    if (expediente?.cliente?.celular) {
      const success = await copyToClipboard(expediente.cliente.celular);
      if (success) {
        showSuccess('Celular copiado al portapapeles');
      } else {
        showError('Error al copiar al portapapeles');
      }
    }
  };

  // Manejar copia de nombre
  const handleCopyNombre = async () => {
    if (expediente) {
      const success = await copyToClipboard(expediente.cliente.nombre);
      if (success) {
        showSuccess('Nombre copiado al portapapeles');
      } else {
        showError('Error al copiar al portapapeles');
      }
    }
  };

  // Manejar copia de DNI
  const handleCopyDNI = async () => {
    if (expediente) {
      const success = await copyToClipboard(expediente.cliente.dni);
      if (success) {
        showSuccess('DNI copiado al portapapeles');
      } else {
        showError('Error al copiar al portapapeles');
      }
    }
  };

  // Manejar copia de dirección
  const handleCopyDireccionExpediente = async () => {
    const dir = resolverDireccionExpediente(expediente);
    if (dir?.texto) {
      const success = await copyToClipboard(dir.texto);
      if (success) {
        showSuccess('Dirección copiada al portapapeles');
      } else {
        showError('Error al copiar al portapapeles');
      }
    }
  };



  // Manejar asignación de parte
  const handleAsignarParte = async (parteId) => {
    try {
      setSelectedParteId(parteId);
      // Cargar lista de personal
      const response = await fetch(`${config.apiUrl}/user?rol=PERSONAL`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      
      // Asegurar que personalList sea un array
      const personalArray = Array.isArray(data) ? data : (data.usuarios || data.data || []);
      setPersonalList(personalArray);
      setShowAsignarModal(true);
    } catch (err) {
      showError('Error al cargar personal: ' + err.message);
    }
  };

  // Manejar selección de personal
  const handleSeleccionarPersonal = (usuario) => {
    setSelectedUsuario(usuario);
    
    // Obtener la fecha límite actual de la parte seleccionada
    const parteSeleccionada = partes.find(p => p.id === selectedParteId);
    if (parteSeleccionada) {
      const fechaActual = new Date(parteSeleccionada.fechaLimite);
      const fechaFormateada = fechaActual.toISOString().split('T')[0];
      setFechaLimitePersonalizada(fechaFormateada);
      setHoraLimitePersonalizada(horaLimiteDesdeIso(parteSeleccionada.fechaLimite));
    }
    
    setShowConfirmacionModal(true);
  };

  // Manejar completar parte (solo para JEFE y ADMIN)
  const handleCompletarParte = async (parteId) => {
    try {
      const confirmacion = await showConfirm('¿Estás seguro de que quieres marcar esta parte como completada?');
      if (!confirmacion) return;

      await partesAPI.updateParte(parteId, {
        estado: 'COMPLETADA',
        fechaCompletado: new Date().toISOString()
      });
      
      // Recargar datos del expediente
      await loadExpedienteData();
      showSuccess('Parte marcada como completada exitosamente');
    } catch (error) {
      console.error('Error al completar parte:', error);
      showError('Error al marcar la parte como completada');
    }
  };

  // Confirmar asignación
  const handleConfirmarAsignacion = async () => {
    try {
      // Validar que se haya seleccionado una fecha límite
      if (!fechaLimitePersonalizada) {
        showError('Por favor selecciona una fecha límite para la parte');
        return;
      }
      if (!horaLimitePersonalizada) {
        showError('Por favor selecciona la hora límite');
        return;
      }

      const dataToUpdate = { 
        asignadoA: selectedUsuario.id,
        fechaLimite: fechaLimiteConHoraDesdeInput(fechaLimitePersonalizada, horaLimitePersonalizada)
      };
      
      await partesAPI.updateParte(selectedParteId, dataToUpdate);
      setShowAsignarModal(false);
      setShowConfirmacionModal(false);
      setSelectedParteId(null);
      setSelectedUsuario(null);
      setFechaLimitePersonalizada('');
      setHoraLimitePersonalizada('18:00');
      loadExpedienteData(); // Recargar datos
      
      // Mostrar mensaje específico para reasignación
      const parteActual = partes.find(p => p.id === selectedParteId);
      const mensaje = parteActual?.asignado?.id 
        ? `Parte reasignada exitosamente a ${selectedUsuario.nombre} con límite ${fechaLimitePersonalizada} ${horaLimitePersonalizada}. El expediente ahora está pendiente de revisión.`
        : `Parte asignada exitosamente a ${selectedUsuario.nombre} con límite ${fechaLimitePersonalizada} ${horaLimitePersonalizada}`;
      
      showSuccess(mensaje);
    } catch (err) {
      showError('Error al asignar parte: ' + err.message);
    }
  };

  // Manejar eliminación de parte
  const handleDeleteParte = async (parteId) => {
    const confirmed = await showConfirm('¿Estás seguro de que quieres eliminar esta parte?');
    if (confirmed) {
      try {
        await partesAPI.deleteParte(parteId);
        loadExpedienteData(); // Recargar datos
      } catch (err) {
        showError('Error al eliminar parte: ' + err.message);
      }
    }
  };

  // Manejar eliminación de archivo
  const handleDeleteArchivo = async (archivoId) => {
    const confirmed = await showConfirm('¿Estás seguro de que quieres eliminar este archivo?');
    if (confirmed) {
      try {
        await archivosAPI.deleteArchivo(archivoId);
        loadExpedienteData(); // Recargar datos
      } catch (err) {
        showError('Error al eliminar archivo: ' + err.message);
      }
    }
  };

  const costosExtra = expediente?.costoExtra || [];

  const handleDeleteCostoExtra = async (costoId) => {
    const confirmed = await showConfirm('¿Eliminar este costo adicional?');
    if (!confirmed) return;
    try {
      await costosExpedienteAPI.deleteCosto(costoId);
      showSuccess('Costo adicional eliminado');
      loadExpedienteData();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Error al eliminar';
      showError(msg);
    }
  };

  const openCostoExtraForm = (costo = null) => {
    setEditingCostoExtra(costo);
    setShowCostoExtraForm(true);
    setShowAbonoForm(false);
  };

  const closeCostoExtraForm = () => {
    setShowCostoExtraForm(false);
    setEditingCostoExtra(null);
  };

  // Manejar eliminación de abono
  const handleDeleteAbono = async (abonoId) => {
    const confirmed = await showConfirm('¿Estás seguro de que quieres eliminar este abono?');
    if (confirmed) {
      try {
        await abonosAPI.deleteAbono(abonoId);
        loadExpedienteData(); // Recargar datos
        showSuccess('Abono eliminado exitosamente');
      } catch (err) {
        showError('Error al eliminar abono: ' + err.message);
      }
    }
  };

  // Manejar apertura del control de versiones
  const handleOpenVersionControl = (archivoId) => {
    setSelectedArchivoId(archivoId);
    setShowVersionControl(true);
  };

  // Manejar cierre del control de versiones
  const handleCloseVersionControl = () => {
    setShowVersionControl(false);
    setSelectedArchivoId(null);
  };

  // Manejar versión creada
  const handleVersionCreated = () => {
    loadExpedienteData(); // Recargar datos del expediente
  };

  // Función auxiliar para construir el nombre de la carpeta del expediente
  // Debe coincidir con la lógica del backend en createExpedienteFolders.js
  const getExpedienteFolderName = () => {
    if (!expediente) return '';
    
    // Eliminar caracteres especiales y reemplazar espacios con guiones bajos
    // Esto debe coincidir exactamente con el backend
    const numeroExpediente = expediente.numero.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
    const nombreCliente = expediente.cliente?.nombre?.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_') || '';
    const tipoExpediente = expediente.tipo?.nombre?.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_') || '';
    
    return `${numeroExpediente}_${nombreCliente}_${tipoExpediente}`;
  };

  // Manejar clic en archivo del explorador
  const handleFileClick = (file) => {
    if (!file || file.isFolder) return;
    
    // Usar la ruta completa que viene del FolderExplorer
    const filePath = file.fullPath || file.name;
    
    // Obtener el nombre de la carpeta del expediente
    const expedienteFolderName = getExpedienteFolderName();
    
    const cacheBust = file.lastModified
      ? new Date(file.lastModified).getTime()
      : Date.now();
    const fileUrl = `${config.uploadsUrl}/${expedienteFolderName}/${filePath}?v=${cacheBust}`;
    
    console.log('📁 Abriendo archivo:', {
      fileName: file.name,
      fullPath: filePath,
      expedienteFolderName,
      url: fileUrl,
      isVisualizable: isVisualizableFile(file.name)
    });
    
    // Verificar si el archivo es visualizable
    if (isVisualizableFile(file.name)) {
      // Abrir en nueva pestaña para visualizar
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    } else {
      // Descargar directamente usando fetch para forzar descarga
      fetch(fileUrl, { cache: 'no-store' })
        .then(response => {
          if (!response.ok) throw new Error('Archivo no encontrado');
          return response.blob();
        })
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = file.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch(error => {
          console.error('Error descargando archivo:', error);
          showError('Error al descargar el archivo: ' + error.message);
        });
    }
  };

  // Función para descargar archivo (forzar descarga incluso para visualizables)
  const handleDownloadFile = (file) => {
    if (!file || file.isFolder) return;
    
    const filePath = file.fullPath || file.name;
    const expedienteFolderName = getExpedienteFolderName();
    const cacheBust = file.lastModified
      ? new Date(file.lastModified).getTime()
      : Date.now();
    const fileUrl = `${config.uploadsUrl}/${expedienteFolderName}/${filePath}?v=${cacheBust}`;
    
    console.log('⬇️ Descargando archivo:', {
      fileName: file.name,
      expedienteFolderName,
      url: fileUrl
    });
    
    fetch(fileUrl, { cache: 'no-store' })
      .then(response => {
        if (!response.ok) throw new Error('Archivo no encontrado');
        return response.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        showSuccess(`Archivo "${file.name}" descargado`);
      })
      .catch(error => {
        console.error('Error descargando archivo:', error);
        showError('Error al descargar el archivo: ' + error.message);
      });
  };

  // Funci├│n para obtener el badge del estado del expediente
  const getEstadoExpedienteBadge = (estado) => {
    const badges = {
      PENDIENTE: { className: 'badge-warning', text: 'Pendiente' },
      EN_PROGRESO: { className: 'badge-info', text: 'En Progreso' },
      COMPLETADO: { className: 'badge-success', text: 'Completado' },
      REASIGNADO: { className: 'badge-secondary', text: 'Pendiente de nuevo' }
    };
    return badges[estado] || { className: 'badge-secondary', text: estado };
  };

  const visitaSeleccionada =
    visitasExpediente.find((v) => v.id === visitaSeleccionadaId) || null;
  const notaSeleccionada = notas.find((n) => n.id === notaSeleccionadaId) || null;
  const uploadsBaseUrl = `${config.apiUrl.replace('/api', '')}/uploads`;

  if (loading) {
    return <div className="loading">Cargando expediente...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  if (!expediente) {
    return <div className="error">Expediente no encontrado</div>;
  }

  return (
    <div>
      {/* Mensaje informativo para usuarios PERSONAL */}
      {isPersonalUser && (
        <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
          <strong>👁 Vista de Solo Lectura:</strong> Como usuario del personal, puedes ver la información del expediente pero no realizar modificaciones.
        </div>
      )}

      {/* Header del expediente */}
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="card-title mb-2">
                Expediente: {expediente.numero}
              </h2>
              {expediente.estado && (
                <div className="estado-expediente">
                  <span className={`badge ${getEstadoExpedienteBadge(expediente.estado).className}`} style={{
                    fontSize: '0.875rem',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontWeight: '500'
                  }}>
                    {getEstadoExpedienteBadge(expediente.estado).text}
                  </span>
                </div>
              )}
            </div>
            <div className="card-actions">
              {!isPersonalUser && (
                <Link to={`/expedientes/${id}/editar`} className="btn btn-secondary">
                  Editar
                </Link>
              )}
              <Link to="/expedientes" className="btn btn-primary">
                Volver a Expedientes
              </Link>
            </div>
          </div>
        </div>
      </div>

              {/* Tabs Mejoradas */}
      <div className="card">
        <div 
          className="tabs-modern"
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '8px' : '0',
            padding: isMobile ? '10px' : '0'
          }}
        >
          <button
            className={`tab-modern ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            <i className="fas fa-info-circle"></i>
            <span className="tab-modern-text-full">General</span>
            <span className="tab-modern-text-short">Info</span>
          </button>
          <button
            className={`tab-modern ${activeTab === 'partes' ? 'active' : ''}`}
            onClick={() => setActiveTab('partes')}
          >
            <i className="fas fa-tasks"></i>
            <span className="tab-modern-text-full">Partes ({partes.length})</span>
            <span className="tab-modern-text-short">({partes.length})</span>
          </button>
          {!isPersonalUser && (
            <button
              className={`tab-modern ${activeTab === 'abonos' ? 'active' : ''}`}
              onClick={() => setActiveTab('abonos')}
            >
              <i className="fas fa-credit-card"></i>
              <span className="tab-modern-text-full">Abonos ({abonos.length})</span>
              <span className="tab-modern-text-short">({abonos.length})</span>
            </button>
          )}
          <button
            className={`tab-modern ${activeTab === 'archivos' ? 'active' : ''}`}
            onClick={() => setActiveTab('archivos')}
          >
            <i className="fas fa-folder-open"></i>
            <span className="tab-modern-text-full">Archivos</span>
            <span className="tab-modern-text-short"></span>
          </button>
          <button
            className={`tab-modern ${activeTab === 'notas' ? 'active' : ''}`}
            onClick={() => setActiveTab('notas')}
          >
            <i className="fas fa-sticky-note"></i>
            <span className="tab-modern-text-full">Notas ({notas.length})</span>
            <span className="tab-modern-text-short">({notas.length})</span>
          </button>
        </div>

        {/* Contenido de tabs */}
        <div className="tab-content active">
          {/* Tab General - Mejorado */}
          {activeTab === 'general' && (
            <>
            <div className="general-grid">
              <div className="general-info-columns">
              <div className="general-card-header">
                <i className="fas fa-info-circle card-icon"></i>
                <h3>Información general</h3>
                <small>Datos principales</small>
              </div>
              {/* Información del Cliente */}
              <div className="info-card client-card">
                <div className="card-header-modern">
                  <i className="fas fa-user-circle card-icon"></i>
                  <h3>Información del Cliente</h3>
                </div>
                <div className="card-content">
                  <div className="info-item">
                    <i className="fas fa-user info-icon"></i>
                    <div className="info-text">
                      <label>Nombre</label>
                      <div className="phone-container">
                        <span>{expediente.cliente.nombre}</span>
                        <button
                          className="btn-copy"
                          onClick={handleCopyNombre}
                          title="Copiar nombre"
                        >
                          <i className="fas fa-copy"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="info-item">
                    <i className="fas fa-id-card info-icon"></i>
                    <div className="info-text">
                      <label>DNI</label>
                      <div className="phone-container">
                        <span>{expediente.cliente.dni}</span>
                        <button
                          className="btn-copy"
                          onClick={handleCopyDNI}
                          title="Copiar DNI"
                        >
                          <i className="fas fa-copy"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="info-item">
                    <i className="fas fa-phone info-icon"></i>
                    <div className="info-text">
                      <label>Celular</label>
                      <div className="phone-container">
                        <span>{expediente.cliente.celular || 'No registrado'}</span>
                        {expediente.cliente.celular && (
                        <button
                          className="btn-copy"
                          onClick={handleCopyCelular}
                          title="Copiar número"
                        >
                          <i className="fas fa-copy"></i>
                        </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Información del Expediente */}
              <div className="info-card expediente-card">
                <div className="card-header-modern">
                  <i className="fas fa-file-alt card-icon"></i>
                  <h3>Información del Proyecto</h3>
                </div>
                <div className="card-content">
                  <div className="info-item">
                    <i className="fas fa-tag info-icon"></i>
                    <div className="info-text">
                      <label>Tipo</label>
                      <span className="tipo-badge">{expediente.tipo.nombre}</span>
                      {esPlanosConstruccion && !isPersonalUser && (
                        <button
                          type="button"
                          className="btn-contrato-construccion"
                          onClick={handleGenerarContratoConstruccion}
                          disabled={generandoContrato}
                        >
                          <i className={"fas " + (generandoContrato ? "fa-spinner fa-spin" : "fa-file-word")}></i>
                          {generandoContrato ? 'Generando...' : 'Crear contrato'}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="info-item">
                    <i className="fas fa-calendar-alt info-icon"></i>
                    <div className="info-text">
                      <label>Fecha Límite</label>
                      <span className="fecha-limite">{formatFechaLimite(expediente.fechaLimite)}</span>
                    </div>
                  </div>
                  {!isPersonalUser && (
                    <div className="info-item">
                      <i className="fas fa-money-bill-wave info-icon"></i>
                      <div className="info-text">
                        <label>Monto Total</label>
                        <span className="monto-total">{formatCurrency(expediente.montoTotal)}</span>
                      </div>
                    </div>
                  )}
                  {formatMzLote(expediente.manzana, expediente.lote) && (
                    <div className="info-item">
                      <i className="fas fa-th info-icon"></i>
                      <div className="info-text">
                        <label>Manzana y lote</label>
                        <span>{formatMzLote(expediente.manzana, expediente.lote)}</span>
                      </div>
                    </div>
                  )}
                  {expediente.sector && (
                    <div className="info-item">
                      <i className="fas fa-map-signs info-icon"></i>
                      <div className="info-text">
                        <label>Sector</label>
                        <span>{expediente.sector}</span>
                      </div>
                    </div>
                  )}
                  {expediente.distrito && (
                    <div className="info-item">
                      <i className="fas fa-city info-icon"></i>
                      <div className="info-text">
                        <label>Distrito</label>
                        <span>{expediente.distrito}</span>
                      </div>
                    </div>
                  )}
                  {expediente.departamento && (
                    <div className="info-item">
                      <i className="fas fa-globe-americas info-icon"></i>
                      <div className="info-text">
                        <label>Departamento</label>
                        <span>{expediente.departamento}</span>
                      </div>
                    </div>
                  )}
                  {etiquetaTipoProyecto(expediente) && (
                    <div className="info-item">
                      <i className="fas fa-building info-icon"></i>
                      <div className="info-text">
                        <label>Tipo de proyecto</label>
                        <span>{etiquetaTipoProyecto(expediente)}</span>
                      </div>
                    </div>
                  )}
                  {expediente.metrosCuadrados != null && expediente.metrosCuadrados !== '' && (
                    <div className="info-item">
                      <i className="fas fa-ruler-combined info-icon"></i>
                      <div className="info-text">
                        <label>Metros cuadrados</label>
                        <span>{Number(expediente.metrosCuadrados).toLocaleString('es-PE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} m²</span>
                      </div>
                    </div>
                  )}
                  {direccionExpediente && (
                    <div className="info-item">
                      <i className="fas fa-map-marker-alt info-icon"></i>
                      <div className="info-text">
                        <label>
                          {direccionExpediente.esFallbackCliente
                            ? 'Dirección'
                            : 'Dirección del expediente'}
                        </label>
                        {direccionExpediente.esFallbackCliente && (
                          <p className="text-muted small mb-1" style={{ marginTop: '-2px' }}>
                            Tomada del registro del cliente (expediente sin dirección propia)
                          </p>
                        )}
                        <div className="phone-container">
                          <span>{direccionExpediente.texto}</span>
                          <button
                            type="button"
                            className="btn-copy"
                            onClick={handleCopyDireccionExpediente}
                            title="Copiar dirección"
                          >
                            <i className="fas fa-copy"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {expediente.ubicacionUrl && !esSubdivision && (
                    <div className="info-item">
                      <i className="fas fa-map info-icon"></i>
                      <div className="info-text">
                        <label>Ubicación en mapa</label>
                        <a 
                          href={expediente.ubicacionUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn-map"
                        >
                          <i className="fas fa-external-link-alt"></i>
                          Ver en Mapa
                        </a>
                      </div>
                    </div>
                  )}
                  {/* Botón para ir a Programa de Subdivisiones */}
                  {esSubdivision && (
                    <div className="info-item">
                      <i className="fas fa-code-branch info-icon"></i>
                      <div className="info-text">
                        <label>Programa de Subdivisiones</label>
                        <button
                          onClick={handleIrAPrograma}
                          className="btn btn-primary btn-sm"
                          style={{
                            marginTop: '8px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                          title="Abrir en Programa de Subdivisiones con datos precargados"
                        >
                          <i className="fas fa-external-link-alt"></i>
                          Ir a Programa
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              </div>
              {/* Estado de Pagos */}
              {!isPersonalUser && (
                <div className="info-card payments-card">
                  <div className="card-header-modern">
                    <i className="fas fa-credit-card card-icon"></i>
                    <h3>Estado de Pagos</h3>
                  </div>
                  <div className="card-content">
                    <div className="progress-section">
                      <div className="progress-circle">
                        <div className="progress-ring">
                          <svg width="140" height="140">
                            <circle
                              cx="70"
                              cy="70"
                              r="60"
                              fill="none"
                              stroke="#e9ecef"
                              strokeWidth="8"
                            />
                            <circle
                              cx="70"
                              cy="70"
                              r="60"
                              fill="none"
                              stroke={expediente.porcentajePagado >= 70 ? '#28a745' : expediente.porcentajePagado >= 40 ? '#ffc107' : '#dc3545'}
                              strokeWidth="8"
                              strokeDasharray={`${(expediente.porcentajePagado / 100) * 377} 377`}
                              strokeDashoffset="0"
                              transform="rotate(-90 70 70)"
                            />
                          </svg>
                          <div className="progress-text">
                            <span className="progress-percentage">{expediente.porcentajePagado}%</span>
                            <span className="progress-label">Pagado</span>
                          </div>
                        </div>
                      </div>
                      <div className="payment-details">
                        <div className="payment-item">
                          <span className="payment-label">Total Abonado</span>
                          <span className="payment-amount paid">{formatCurrency(expediente.totalAbonado)}</span>
                        </div>
                        <div className="payment-item">
                          <span className="payment-label">Adeudado</span>
                          <span className="payment-amount pending">{formatCurrency(expediente.adeudado)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Estado del Trabajo */}
              <div className="info-card work-card">
                <div className="card-header-modern">
                  <i className="fas fa-tasks card-icon"></i>
                  <h3>Estado del Trabajo</h3>
                </div>
                <div className="card-content">
                  <div className="progress-section">
                    <div className="progress-circle">
                      <div className="progress-ring">
                        <svg width="140" height="140">
                          <circle
                            cx="70"
                            cy="70"
                            r="60"
                            fill="none"
                            stroke="#e9ecef"
                            strokeWidth="8"
                          />
                          <circle
                            cx="70"
                            cy="70"
                            r="60"
                            fill="none"
                            stroke={expediente.porcentajeAvance >= 70 ? '#28a745' : expediente.porcentajeAvance >= 40 ? '#ffc107' : '#dc3545'}
                            strokeWidth="8"
                            strokeDasharray={`${(expediente.porcentajeAvance / 100) * 377} 377`}
                            strokeDashoffset="0"
                            transform="rotate(-90 70 70)"
                          />
                        </svg>
                        <div className="progress-text">
                          <span className="progress-percentage">{expediente.porcentajeAvance}%</span>
                          <span className="progress-label">Completado</span>
                        </div>
                      </div>
                    </div>
                    <div className="work-details">
                      <div className="work-item">
                        <span className="work-label">Partes Completadas</span>
                        <span className="work-count">{expediente.partesCompletadas} de {expediente.totalPartes}</span>
                      </div>
                      <div className="work-item">
                        <span className="work-label">Estado General</span>
                        <span className={`work-status ${expediente.porcentajeAvance >= 70 ? 'completed' : expediente.porcentajeAvance >= 40 ? 'in-progress' : 'pending'}`}>
                          {expediente.porcentajeAvance >= 70 ? 'Completado' : expediente.porcentajeAvance >= 40 ? 'En Progreso' : 'Pendiente'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </>
          )}

          {/* Tab Partes - Mejorado */}
          {activeTab === 'partes' && (
            <div className="partes-container">
              {/* Header con estadísticas */}
              <div className="partes-header">
                <div className="partes-stats">
                  <div className="stat-item">
                    <i className="fas fa-tasks stat-icon"></i>
                    <div className="stat-content">
                      <span className="stat-number">{partes.length}</span>
                      <span className="stat-label">Total Partes</span>
                    </div>
                  </div>
                  <div className="stat-item">
                    <i className="fas fa-check-circle stat-icon completed"></i>
                    <div className="stat-content">
                      <span className="stat-number">{partes.filter(p => p.estado === 'COMPLETADA').length}</span>
                      <span className="stat-label">Completadas</span>
                    </div>
                  </div>
                  <div className="stat-item">
                    <i className="fas fa-clock stat-icon pending"></i>
                    <div className="stat-content">
                      <span className="stat-number">{partes.filter(p => p.estado === 'PENDIENTE').length}</span>
                      <span className="stat-label">Pendientes</span>
                    </div>
                  </div>
                </div>
                
                {!isPersonalUser && (
                  <button
                    className="btn-add-parte"
                    onClick={() => setShowParteForm(!showParteForm)}
                    title={showParteForm ? 'Cancelar' : 'Agregar Parte'}
                    style={{
                      width: isMobile ? '40px' : 'auto',
                      height: isMobile ? '40px' : 'auto',
                      padding: isMobile ? '0' : '10px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: isMobile ? '0' : '8px',
                      borderRadius: '10px',
                      fontSize: isMobile ? '1.3rem' : '0.95rem'
                    }}
                  >
                    <i className={showParteForm ? 'fas fa-times' : 'fas fa-plus'}></i>
                    {!isMobile && <span>{showParteForm ? 'Cancelar' : 'Agregar Parte'}</span>}
                  </button>
                )}
              </div>

              {showParteForm && !isPersonalUser && (
                <ParteForm
                  expedienteId={id}
                  onParteCreated={() => {
                    loadExpedienteData();
                    setShowParteForm(false);
                  }}
                  onCancel={() => setShowParteForm(false)}
                />
              )}

              {partes.length === 0 ? (
                <div className="empty-state-modern">
                  <i className="fas fa-tasks empty-icon"></i>
                  <h3>No hay partes registradas</h3>
                  <p>Comienza agregando la primera parte del expediente</p>
                </div>
              ) : (
                <div className="partes-grid">
                  {partes.map((parte, index) => (
                    <div key={parte.id} className={`parte-card ${parte.estado.toLowerCase()}`}>
                      <div className="parte-card-header">
                        <div className="parte-number">
                          <span>#{index + 1}</span>
                        </div>
                        <div className="parte-status">
                          <span className={`status-badge ${parte.estado.toLowerCase()}`}>
                            <i className={`fas ${parte.estado === 'COMPLETADO' ? 'fa-check-circle' : parte.estado === 'EN_PROCESO' ? 'fa-clock' : 'fa-hourglass-start'}`}></i>
                            {parte.estado}
                          </span>
                        </div>
                      </div>
                      
                      <div className="parte-card-content">
                        <h4 className="parte-title">{parte.descripcion}</h4>
                        
                        <div className="parte-details">
                          <div className="detail-item">
                            <i className="fas fa-calendar-alt"></i>
                            <span>Fecha límite: {formatFechaLimite(parte.fechaLimite)}</span>
                          </div>
                          
                          {parte.asignado && (
                            <div className="detail-item">
                              <i className="fas fa-user"></i>
                              <span>Asignado a: {parte.asignado?.nombre || 'Usuario no encontrado'}</span>
                            </div>
                          )}
                          
                          {parte.estado === 'COMPLETADO' && parte.fechaCompletado && (
                            <div className="detail-item completed">
                              <i className="fas fa-check-circle"></i>
                              <span>Completado el {formatDate(parte.fechaCompletado)} a las {formatTime(parte.fechaCompletado)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {!isPersonalUser && (
                        <div className="parte-card-actions">
                          <button
                            className={`btn-action ${parte.asignado ? 'btn-reassign' : 'btn-assign'}`}
                            onClick={() => handleAsignarParte(parte.id)}
                            disabled={parte.estado === 'COMPLETADO'}
                            title={parte.asignado ? "Reasignar a otro personal" : "Asignar a personal"}
                          >
                            <i className={`fas ${parte.asignado ? 'fa-user-edit' : 'fa-user-plus'}`}></i>
                            {parte.asignado ? 'Reasignar' : 'Asignar'}
                          </button>
                          
                          {/* Botón para completar parte - Solo para JEFE y ADMIN */}
                          {user?.rol === 'JEFE' || user?.rol === 'ADMIN' ? (
                            <button
                              className={`btn-action ${parte.estado === 'COMPLETADA' ? 'btn-completed' : 'btn-complete'}`}
                              onClick={() => handleCompletarParte(parte.id)}
                              disabled={parte.estado === 'COMPLETADA'}
                              title={parte.estado === 'COMPLETADA' ? "Parte completada" : "Marcar como completada"}
                            >
                              <i className={`fas ${parte.estado === 'COMPLETADA' ? 'fa-check-circle' : 'fa-check'}`}></i>
                            </button>
                          ) : null}
                          
                          <button
                            className="btn-action btn-delete"
                            onClick={() => handleDeleteParte(parte.id)}
                            disabled={parte.estado === 'COMPLETADO'}
                            title="Eliminar parte"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tab Abonos - Mejorado */}
          {activeTab === 'abonos' && !isPersonalUser && (
            <div className="abonos-container">
              {/* Header con estad├¡sticas de pagos */}
              <div className="abonos-header">
                <div className="abonos-stats">
                  <div className="stat-item">
                    <i className="fas fa-credit-card stat-icon"></i>
                    <div className="stat-content">
                      <span className="stat-number">{abonos.length}</span>
                      <span className="stat-label">Total Abonos</span>
                    </div>
                  </div>
                  <div className="stat-item">
                    <i className="fas fa-money-bill-wave stat-icon paid"></i>
                    <div className="stat-content">
                      <span className="stat-number">{formatCurrency(expediente?.totalAbonado || 0)}</span>
                      <span className="stat-label">Total Pagado</span>
                    </div>
                  </div>
                  <div className="stat-item">
                    <i className="fas fa-exclamation-triangle stat-icon pending"></i>
                    <div className="stat-content">
                      <span className="stat-number">{formatCurrency(expediente?.adeudado || 0)}</span>
                      <span className="stat-label">Pendiente</span>
                    </div>
                  </div>
                </div>
                
                <button
                  type="button"
                  className="btn-add-abono"
                  onClick={() => {
                    closeCostoExtraForm();
                    setShowAbonoForm(!showAbonoForm);
                  }}
                  title={showAbonoForm ? 'Cancelar' : 'Registrar Abono'}
                  style={{
                    width: isMobile ? '40px' : 'auto',
                    height: isMobile ? '40px' : 'auto',
                    padding: isMobile ? '0' : '10px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: isMobile ? '0' : '8px',
                    borderRadius: '10px',
                    fontSize: isMobile ? '1.3rem' : '0.95rem'
                  }}
                >
                  <i className={showAbonoForm ? 'fas fa-times' : 'fas fa-plus'}></i>
                  {!isMobile && <span>{showAbonoForm ? 'Cancelar' : 'Registrar Abono'}</span>}
                </button>
                {(user?.rol === 'JEFE' || user?.rol === 'ADMIN') && (
                  <button
                    type="button"
                    className="btn-add-abono"
                    onClick={() => {
                      if (showCostoExtraForm && !editingCostoExtra) closeCostoExtraForm();
                      else openCostoExtraForm(null);
                    }}
                    title="Agregar costo adicional"
                    style={{ marginLeft: '8px', background: '#6c757d' }}
                  >
                    <i className="fas fa-receipt"></i>
                    {!isMobile && <span style={{ marginLeft: '6px' }}>Costo extra</span>}
                  </button>
                )}
              </div>

              <div className="payment-summary-card" style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: '12px', padding: '1rem', marginBottom: '1rem' }}>
                <h4 style={{ margin: '0 0 0.75rem' }}>Resumen de cobro</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Monto base</span>
                    <strong>{formatCurrency(expediente?.montoBase ?? expediente?.montoTotal ?? 0)}</strong>
                  </div>
                  {costosExtra.map((c) => (
                    <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', color: '#6c757d' }}>
                      <span>+ {c.detalle}</span>
                      <span>
                        <strong>{formatCurrency(c.monto)}</strong>
                        {(user?.rol === 'JEFE' || user?.rol === 'ADMIN') && (
                          <>
                            <button type="button" onClick={() => openCostoExtraForm(c)} style={{ marginLeft: 6 }}><i className="fas fa-edit"></i></button>
                            <button type="button" onClick={() => handleDeleteCostoExtra(c.id)} style={{ marginLeft: 4, color: '#dc3545' }}><i className="fas fa-trash"></i></button>
                          </>
                        )}
                      </span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #dee2e6', paddingTop: 8 }}>
                    <strong>Total a pagar</strong>
                    <strong style={{ color: '#0d6efd' }}>{formatCurrency(expediente?.montoAPagar ?? expediente?.montoTotal ?? 0)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Abonado</span>
                    <strong>{formatCurrency(expediente?.totalAbonado || 0)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Pendiente</span>
                    <strong style={{ color: '#dc3545' }}>{formatCurrency(expediente?.adeudado || 0)}</strong>
                  </div>
                </div>
              </div>

              {showCostoExtraForm && (
                <CostoExtraForm
                  expedienteId={id}
                  costo={editingCostoExtra}
                  onSaved={() => {
                    const wasEdit = Boolean(editingCostoExtra);
                    closeCostoExtraForm();
                    loadExpedienteData();
                    showSuccess(wasEdit ? 'Costo actualizado' : 'Costo adicional agregado');
                  }}
                  onCancel={closeCostoExtraForm}
                />
              )}

              {/* Timeline de progreso de pagos */}
              <div className="payment-timeline">
                <div className="timeline-header">
                  <h4>Progreso de Pagos</h4>
                  <div className="progress-percentage">{expediente?.porcentajePagado || 0}% Completado</div>
                </div>
                <div className="progress-container">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${expediente?.porcentajePagado || 0}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              {showAbonoForm && (
                <AbonoForm
                  expedienteId={id}
                  expediente={expediente}
                  abonos={abonos}
                  onConfirmFullPayment={showConfirm}
                  onAbonoCreated={() => {
                    loadExpedienteData();
                    setShowAbonoForm(false);
                  }}
                  onCancel={() => setShowAbonoForm(false)}
                />
              )}

              {abonos.length === 0 ? (
                <div className="empty-state-modern">
                  <i className="fas fa-credit-card empty-icon"></i>
                  <h3>No hay abonos registrados</h3>
                  <p>Comienza registrando el primer abono del expediente</p>
                </div>
              ) : (
                <div className="abonos-timeline">
                  {abonos.map((abono, index) => (
                    <div key={abono.id} className="abono-card">
                      <div className="abono-timeline-marker">
                        <div className="marker-dot"></div>
                        <div className="marker-line"></div>
                      </div>
                      
                      <div className="abono-card-content">
                        <div className="abono-header-modern">
                          <div className="abono-info">
                            <div className="abono-number">Abono #{index + 1}</div>
                            <div className="abono-date">
                              <i className="fas fa-calendar-alt"></i>
                              {formatDate(abono.fecha)}
                            </div>
                          </div>
                          <div className="abono-amount">
                            <span className="amount-value">{formatCurrency(abono.monto)}</span>
                            <span className="amount-label">Monto Pagado</span>
                          </div>
                        </div>
                        
                        {/* Comprobante de Pago (si existe) */}
                        {abono.evidencia && abono.evidencia.trim() !== '' && (
                          <div className="abono-evidence">
                            <div className="evidence-header">
                              <i className="fas fa-file-alt"></i>
                              <span>Comprobante de Pago</span>
                            </div>
                            <div className="evidence-actions">
                              <a 
                                href={`${config.uploadsUrl}/${abono.evidencia}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="btn-evidence view"
                                title="Ver comprobante"
                              >
                                <i className="fas fa-eye"></i>
                                Ver
                              </a>
                              <a 
                                href={`${config.uploadsUrl}/${abono.evidencia}`} 
                                download={!isVisualizableFile(abono.evidencia)}
                                className="btn-evidence download"
                                title="Descargar comprobante"
                              >
                                <i className="fas fa-download"></i>
                                Descargar
                              </a>
                              {!isPersonalUser && !abono.reciboGenerado && (
                                <button
                                  className="btn-evidence delete"
                                  onClick={() => handleDeleteAbono(abono.id)}
                                  title="Eliminar abono"
                                  style={{ background: '#dc3545', color: 'white' }}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Recibo de Abono Generado (si existe) */}
                        {abono.reciboGenerado && abono.reciboGenerado.trim() !== '' && (
                          <div className="abono-evidence">
                            <div className="evidence-header">
                              <i className="fas fa-file-pdf"></i>
                              <span>Recibo de Abono</span>
                            </div>
                            <div className="evidence-actions">
                              <a 
                                href={`${config.uploadsUrl}/recibos-abonos/${abono.reciboGenerado}?v=${encodeURIComponent(abono.updatedAt || abono.id)}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="btn-evidence view"
                                title="Ver recibo"
                              >
                                <i className="fas fa-eye"></i>
                                Ver Recibo
                              </a>
                              <a 
                                href={`${config.uploadsUrl}/recibos-abonos/${abono.reciboGenerado}?v=${encodeURIComponent(abono.updatedAt || abono.id)}`} 
                                download={abono.reciboGenerado}
                                className="btn-evidence download"
                                title="Descargar recibo"
                              >
                                <i className="fas fa-download"></i>
                                Descargar
                              </a>
                              {!isPersonalUser && (
                                <button
                                  className="btn-evidence delete"
                                  onClick={() => handleDeleteAbono(abono.id)}
                                  title="Eliminar abono"
                                  style={{ background: '#dc3545', color: 'white' }}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Botón eliminar para abonos sin evidencia ni recibo */}
                        {!isPersonalUser && !abono.evidencia && !abono.reciboGenerado && (
                          <div className="abono-actions" style={{ marginTop: '15px', textAlign: 'right' }}>
                            <button
                              className="btn-danger btn-sm"
                              onClick={() => handleDeleteAbono(abono.id)}
                              title="Eliminar abono"
                              style={{ 
                                padding: '8px 16px',
                                fontSize: '14px',
                                borderRadius: '6px',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 🆕 Sección de Boleta Final - Visible para JEFE y ADMIN */}
              {(user?.rol === 'JEFE' || user?.rol === 'ADMIN') && (
                <div className="boleta-final-section" style={{ marginTop: '30px', padding: '20px', border: '2px solid #0066cc', borderRadius: '8px', backgroundColor: '#ffffff' }}>
                  <h3 style={{ color: '#0066cc', marginBottom: '20px', fontSize: '20px', fontWeight: 'bold' }}>
                    <i className="fas fa-file-invoice"></i> Boleta Final
                  </h3>

                  {expediente.boletaGenerada ? (
                    <div className="boleta-generada" style={{ backgroundColor: '#e7f3ff', padding: '20px', borderRadius: '8px' }}>
                      <div style={{ marginBottom: '15px' }}>
                        <strong>Número de Boleta:</strong> 
                        <span style={{ fontSize: '20px', color: '#0066cc', marginLeft: '10px', fontWeight: 'bold' }}>
                          {expediente.boletaNumero}
                        </span>
                      </div>
                      <div style={{ marginBottom: '15px' }}>
                        <strong>Fecha de Emisión:</strong> 
                        <span style={{ marginLeft: '10px' }}>{formatDate(expediente.fechaBoleta)}</span>
                      </div>
                      <div style={{ marginBottom: '20px' }}>
                        <strong>Monto:</strong> 
                        <span style={{ marginLeft: '10px', fontSize: '18px', color: '#28a745', fontWeight: 'bold' }}>
                          {formatCurrency(expediente.montoAPagar ?? expediente.montoTotal)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                        <a 
                          href={`https://www.homesconstructora.com/sistema-boletas/${expediente.boletaPDF}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none' }}
                        >
                          <button style={{ padding: '10px 20px', backgroundColor: '#0066cc', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>
                            <i className="fas fa-eye"></i> Ver Boleta
                          </button>
                        </a>
                        <a 
                          href={`https://www.homesconstructora.com/sistema-boletas/${expediente.boletaPDF}`} 
                          download
                          style={{ textDecoration: 'none' }}
                        >
                          <button style={{ padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>
                            <i className="fas fa-download"></i> Descargar
                          </button>
                        </a>
                      </div>
                      <div style={{ color: '#28a745', fontWeight: 'bold', fontSize: '14px' }}>
                        <i className="fas fa-check-circle"></i> Boleta final generada y registrada
                      </div>
                    </div>
                  ) : (
                    <div className="boleta-pendiente">
                      {expediente.porcentajePagado >= 100 ? (
                        <>
                          <div style={{ marginBottom: '15px', color: '#28a745', fontSize: '16px' }}>
                            <i className="fas fa-check-circle"></i> 
                            <strong> El expediente está completamente pagado.</strong>
                          </div>
                          <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                              Fecha de emisión
                            </label>
                            <input
                              type="date"
                              value={fechaEmisionBoleta}
                              max={obtenerFechaPeruHoy()}
                              onChange={(e) => setFechaEmisionBoleta(e.target.value)}
                              style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px' }}
                            />
                          </div>
                          <button
                            onClick={handleGenerarBoletaFinal}
                            disabled={generandoBoleta}
                            style={{
                              padding: '15px 30px',
                              backgroundColor: generandoBoleta ? '#ccc' : '#0066cc',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              fontSize: '16px',
                              fontWeight: 'bold',
                              cursor: generandoBoleta ? 'not-allowed' : 'pointer',
                              transition: 'background-color 0.3s'
                            }}
                          >
                            <i className="fas fa-file-invoice"></i> 
                            {generandoBoleta ? ' Generando...' : ' Generar Boleta Final'}
                          </button>
                        </>
                      ) : (
                        <div style={{ padding: '20px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
                          <div style={{ marginBottom: '10px' }}>
                            <i className="fas fa-clock" style={{ color: '#856404', marginRight: '8px' }}></i>
                            <span style={{ color: '#856404', fontWeight: 'bold' }}>
                              La boleta final se podrá generar cuando el pago esté completo.
                            </span>
                          </div>
                          <div style={{ fontSize: '16px', marginTop: '15px' }}>
                            <span style={{ color: '#6c757d' }}>Falta: </span>
                            <span style={{ fontWeight: 'bold', color: '#dc3545', fontSize: '18px' }}>
                              {formatCurrency(expediente.adeudado)}
                            </span>
                            <span style={{ marginLeft: '15px', color: '#6c757d' }}>
                              ({expediente.porcentajePagado.toFixed(2)}% pagado)
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Tab Archivos - Mejorado */}
          {activeTab === 'archivos' && (
            <div className="archivos-container">
              {/* Explorador de Carpetas Mejorado */}
              <div className="folder-explorer-container">
                <FolderExplorer 
                  expedienteId={id}
                  expedienteNumero={expediente?.numero}
                  initialFolderHint={initialFolderHint}
                  onFileClick={handleFileClick}
                  onDownloadFile={handleDownloadFile}
                />
              </div>
            </div>
          )}

          {/* Tab Notas - Nuevo */}
          {activeTab === 'notas' && (
            <div className="notas-container">
              {/* Header con botón de nueva nota */}
              <div className="notas-header">
                <div className="notas-info">
                  <div className="info-icon-container">
                    <i className="fas fa-sticky-note"></i>
                  </div>
                  <div className="info-content">
                    <h4>Notas del Expediente</h4>
                    <p>Comunicación y seguimiento del expediente</p>
                  </div>
                </div>
                <div className="notas-actions">
                  <button
                    className="btn-add-nota"
                    onClick={() => setShowNotaForm(true)}
                    title={isMobile ? "Agregar nota" : "Nueva Nota"}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: isMobile ? '0' : '8px',
                      width: isMobile ? '40px' : 'auto',
                      height: isMobile ? '40px' : 'auto',
                      padding: isMobile ? '0' : '0.5rem 1rem',
                      background: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: isMobile ? '1.3rem' : '0.95rem'
                    }}
                  >
                    <i className="fas fa-plus"></i>
                    {!isMobile && <span>Nota</span>}
                  </button>
                </div>
              </div>

              {/* Sección de Levantamiento de Requisitos - Solo para Plano de Construcción */}
              {expediente?.tipo?.nombre?.toUpperCase() === 'PLANO DE CONSTRUCCION' && (
              <div className="levantamiento-section" style={{
                marginBottom: isMobile ? '1rem' : '2rem',
                padding: isMobile ? '12px' : '1rem',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h5 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: isMobile ? '0.9rem' : '1.1rem' }}>
                  <i className="fas fa-file-alt"></i>
                  Levantamiento de Requisitos
                </h5>
                
                {levantamiento ? (
                  <a
                    href={`${config.apiUrl.replace('/api', '')}/uploads/${levantamiento.archivoPath}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    title={isMobile ? `Ver: ${levantamiento.nombreOriginal}` : `Ver Levantamiento de Requisitos`}
                    style={{
                      width: isMobile ? '40px' : 'auto',
                      height: isMobile ? '40px' : 'auto',
                      padding: isMobile ? '0' : '0.5rem 1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: isMobile ? '0' : '6px',
                      textDecoration: 'none',
                      borderRadius: '10px',
                      fontSize: isMobile ? '1.2rem' : '0.95rem'
                    }}
                  >
                    <i className="fas fa-eye"></i>
                    {!isMobile && <span>Ver</span>}
                  </a>
                ) : (
                  <label
                    htmlFor="levantamiento-input"
                    className="btn btn-success"
                    title={isMobile ? 'Subir levantamiento' : 'Subir Levantamiento de Requisitos'}
                    style={{
                      width: isMobile ? '40px' : 'auto',
                      height: isMobile ? '40px' : 'auto',
                      padding: isMobile ? '0' : '0.5rem 1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: isMobile ? '0' : '6px',
                      cursor: 'pointer',
                      margin: 0,
                      borderRadius: '10px',
                      fontSize: isMobile ? '1.3rem' : '0.95rem'
                    }}
                  >
                    <i className="fas fa-upload"></i>
                    {!isMobile && <span>{subiendoLevantamiento ? 'Subiendo...' : 'Subir'}</span>}
                    <input
                      id="levantamiento-input"
                      type="file"
                      style={{ display: 'none' }}
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          try {
                            setSubiendoLevantamiento(true);
                            const formData = new FormData();
                            formData.append('archivo', file);
                            await levantamientoAPI.subirLevantamiento(id, formData);
                            showSuccess('Levantamiento de requisitos subido exitosamente');
                            loadExpedienteData();
                          } catch (err) {
                            console.error('Error al subir levantamiento:', err);
                            showError(err.response?.data?.error || 'Error al subir levantamiento');
                          } finally {
                            setSubiendoLevantamiento(false);
                            e.target.value = ''; // Reset input
                          }
                        }
                      }}
                      accept=".pdf,.doc,.docx,.xls,.xlsx"
                    />
                  </label>
                )}
              </div>
              )}

              {isMobile ? (
                <>
                  <div
                    className="visitas-expediente-notas"
                    style={{
                      marginBottom: '1.5rem',
                      padding: '12px',
                      background: '#fff',
                      borderRadius: '8px',
                      border: '1px solid #dee2e6',
                    }}
                  >
                    <h5
                      style={{
                        margin: '0 0 0.35rem 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '0.95rem',
                        color: '#2c3e50',
                      }}
                    >
                      <i className="fas fa-calendar-check" />
                      Citas vinculadas a este expediente
                    </h5>
                    {visitasExpediente.length === 0 ? (
                      <p style={{ margin: 0, color: '#6c757d', fontSize: '0.9rem' }}>
                        No hay citas con este expediente asignado.
                      </p>
                    ) : (
                      (mostrarTodasVisitas ? visitasExpediente : visitasExpediente.slice(0, 4)).map((v) => {
                        const lab = etiquetaAsistencia(v.clienteAsistio);
                        const registrada =
                          v.clienteAsistio === true || v.clienteAsistio === false;

                        if (registrada) {
                          return (
                            <VisitaAsistenciaResumenNota
                              key={v.id}
                              visita={v}
                              lab={lab}
                              puedeEliminar={!isPersonalUser}
                              showConfirm={showConfirm}
                              showError={showError}
                              onActualizada={(data) => {
                                setVisitasExpediente((prev) =>
                                  prev.map((x) =>
                                    x.id === data.id ? { ...x, ...data } : x
                                  )
                                );
                                showSuccess('Registro de asistencia eliminado');
                              }}
                            />
                          );
                        }

                        return (
                          <div key={v.id} className="visita-asistencia-card">
                            <div className="visita-asistencia-card-header">
                              <strong>
                                {formatDate(v.fecha)} · {v.hora}
                              </strong>
                              <span style={{ color: '#495057' }}>{v.tipo}</span>
                              <span className={`visita-asistencia-badge ${lab.clase}`}>
                                {lab.texto}
                              </span>
                            </div>
                            {!isPersonalUser ? (
                              <VisitaAsistenciaEditor
                                visitaId={v.id}
                                initialClienteAsistio={v.clienteAsistio}
                                initialComentarioAsistencia={v.comentarioAsistencia}
                                readOnly={false}
                                compact
                                onGuardado={(data) => {
                                  setVisitasExpediente((prev) =>
                                    prev.map((x) =>
                                      x.id === data.id ? { ...x, ...data } : x
                                    )
                                  );
                                  showSuccess('Asistencia guardada');
                                }}
                              />
                            ) : (
                              <p
                                style={{
                                  margin: '0.35rem 0 0 0',
                                  fontSize: '0.9rem',
                                  color: '#6c757d',
                                }}
                              >
                                Pendiente de registro por el equipo.
                              </p>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                  <NotasList
                    notas={notas}
                    expandable
                    previewLimit={6}
                    collapsedChars={220}
                    onEliminar={async (notaId) => {
                      try {
                        const confirmado = await showConfirm(
                          '¿Estás seguro de que quieres eliminar esta nota?'
                        );
                        if (!confirmado) return;

                        await notasAPI.deleteNota(notaId);
                        showSuccess('Nota eliminada exitosamente');
                        loadExpedienteData();
                      } catch (err) {
                        console.error('Error al eliminar nota:', err);
                        showError(err.response?.data?.error || 'Error al eliminar la nota');
                      }
                    }}
                    usuarioActual={user}
                  />
                </>
              ) : (
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div style={{ border: '1px solid #dee2e6', borderRadius: '10px', background: '#fff', padding: '0.8rem' }}>
                    <h5 style={{ margin: '0 0 .6rem 0', fontSize: '1rem' }}>
                      <i className="fas fa-calendar-check me-2" />
                      Citas (Master-Detail)
                    </h5>
                    <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '0.8rem' }}>
                      <div style={{ borderRight: '1px solid #e9ecef', paddingRight: '.6rem', maxHeight: '420px', overflowY: 'auto' }}>
                        {visitasExpediente.length === 0 ? (
                          <p className="text-muted small mb-0">No hay citas vinculadas.</p>
                        ) : visitasExpediente.map((v) => {
                          const lab = etiquetaAsistencia(v.clienteAsistio);
                          return (
                            <button
                              key={v.id}
                              type="button"
                              className={`btn w-100 text-start mb-2 ${visitaSeleccionadaId === v.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                              onClick={() => setVisitaSeleccionadaId(v.id)}
                            >
                              <div className="fw-semibold small">{formatDate(v.fecha)} · {v.hora}</div>
                              <div className="small">{v.tipo}</div>
                              <div className="small">{lab.texto}</div>
                            </button>
                          );
                        })}
                      </div>
                      <div>
                        {!visitaSeleccionada ? (
                          <p className="text-muted small mb-0">Selecciona una cita para ver detalle.</p>
                        ) : (
                          (() => {
                            const lab = etiquetaAsistencia(visitaSeleccionada.clienteAsistio);
                            const registrada =
                              visitaSeleccionada.clienteAsistio === true ||
                              visitaSeleccionada.clienteAsistio === false;
                            if (registrada) {
                              return (
                                <VisitaAsistenciaResumenNota
                                  visita={visitaSeleccionada}
                                  lab={lab}
                                  puedeEliminar={!isPersonalUser}
                                  showConfirm={showConfirm}
                                  showError={showError}
                                  onActualizada={(data) => {
                                    setVisitasExpediente((prev) =>
                                      prev.map((x) => (x.id === data.id ? { ...x, ...data } : x))
                                    );
                                    showSuccess('Registro de asistencia eliminado');
                                  }}
                                />
                              );
                            }
                            return !isPersonalUser ? (
                              <VisitaAsistenciaEditor
                                visitaId={visitaSeleccionada.id}
                                initialClienteAsistio={visitaSeleccionada.clienteAsistio}
                                initialComentarioAsistencia={visitaSeleccionada.comentarioAsistencia}
                                readOnly={false}
                                onGuardado={(data) => {
                                  setVisitasExpediente((prev) =>
                                    prev.map((x) => (x.id === data.id ? { ...x, ...data } : x))
                                  );
                                  showSuccess('Asistencia guardada');
                                }}
                              />
                            ) : <p className="text-muted small mb-0">Pendiente de registro por el equipo.</p>;
                          })()
                        )}
                      </div>
                    </div>
                  </div>

                  <div style={{ border: '1px solid #dee2e6', borderRadius: '10px', background: '#fff', padding: '0.8rem' }}>
                    <h5 style={{ margin: '0 0 .6rem 0', fontSize: '1rem' }}>
                      <i className="fas fa-sticky-note me-2" />
                      Notas (Master-Detail)
                    </h5>
                    <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '0.8rem' }}>
                      <div style={{ borderRight: '1px solid #e9ecef', paddingRight: '.6rem', maxHeight: '420px', overflowY: 'auto' }}>
                        {notas.length === 0 ? (
                          <p className="text-muted small mb-0">No hay notas registradas.</p>
                        ) : notas.map((n) => (
                          <button
                            key={n.id}
                            type="button"
                            className={`btn w-100 text-start mb-2 ${notaSeleccionadaId === n.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setNotaSeleccionadaId(n.id)}
                          >
                            <div className="fw-semibold small">{n.autor?.nombre || 'Usuario'}</div>
                            <div className="small">{formatDate(n.createdAt)} · {formatTime(n.createdAt)}</div>
                            <div className="small text-truncate">{String(n.contenido || '').slice(0, 60)}</div>
                          </button>
                        ))}
                      </div>
                      <div>
                        {!notaSeleccionada ? (
                          <p className="text-muted small mb-0">Selecciona una nota para ver detalle.</p>
                        ) : (
                          <div className="nota-card" style={{ marginBottom: 0 }}>
                            <div className="nota-card-header">
                              <div className="nota-card-author">
                                <i className="fas fa-user-circle" />
                                <div>
                                  <strong>{notaSeleccionada.autor?.nombre || 'Usuario'}</strong>
                                  <span className="nota-card-rol">{notaSeleccionada.autor?.rol || ''}</span>
                                </div>
                              </div>
                              <div className="nota-card-meta">
                                <span className="nota-card-fecha">
                                  <i className="fas fa-calendar-alt" />
                                  {formatDate(notaSeleccionada.createdAt)} - {formatTime(notaSeleccionada.createdAt)}
                                </span>
                              </div>
                            </div>
                            <div className="nota-card-body">
                              <div className="nota-card-contenido" style={{ whiteSpace: 'pre-wrap' }}>
                                {notaSeleccionada.contenido}
                              </div>
                              {notaSeleccionada.archivoAdjunto ? (
                                <div style={{ marginTop: '.75rem', display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
                                  {(() => {
                                    let archivos = [];
                                    try {
                                      archivos = JSON.parse(notaSeleccionada.archivoAdjunto);
                                    } catch (e) {
                                      archivos = [{ nombre: 'Archivo adjunto', ruta: notaSeleccionada.archivoAdjunto }];
                                    }
                                    return (Array.isArray(archivos) ? archivos : [archivos]).map((archivo, idx) => (
                                      <a
                                        key={`${notaSeleccionada.id}-${idx}`}
                                        href={`${uploadsBaseUrl}/${archivo.ruta || archivo}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-outline-primary"
                                      >
                                        <i className="fas fa-paperclip me-1" />
                                        {archivo.nombre || `Archivo ${idx + 1}`}
                                      </a>
                                    ));
                                  })()}
                                </div>
                              ) : null}
                              {(user?.id === notaSeleccionada.autor?.id || ['JEFE', 'ADMIN'].includes(user?.rol)) && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger mt-3"
                                  onClick={async () => {
                                    try {
                                      const confirmado = await showConfirm(
                                        '¿Estás seguro de que quieres eliminar esta nota?'
                                      );
                                      if (!confirmado) return;
                                      await notasAPI.deleteNota(notaSeleccionada.id);
                                      showSuccess('Nota eliminada exitosamente');
                                      loadExpedienteData();
                                    } catch (err) {
                                      console.error('Error al eliminar nota:', err);
                                      showError(err.response?.data?.error || 'Error al eliminar la nota');
                                    }
                                  }}
                                >
                                  <i className="fas fa-trash me-1" />
                                  Eliminar nota
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Modal de nueva nota */}
              {showNotaForm && (
                <NotaForm
                  expedienteId={id}
                  expedienteNumero={expediente?.numero}
                  onNotaCreated={() => {
                    loadExpedienteData();
                    setShowNotaForm(false);
                  }}
                  onCancel={() => setShowNotaForm(false)}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Control de Versiones - SOLO en gestión de parte asignada */}
      {/* {showVersionControl && selectedArchivoId && (
        <VersionControl
          archivoId={selectedArchivoId}
          onVersionCreated={handleVersionCreated}
          onClose={handleCloseVersionControl}
        />
      )} */}

      {/* Modal de Asignaci├│n de Parte */}
      {showAsignarModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              backgroundColor: '#007bff',
              color: 'white',
              padding: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px'
            }}>
              <h5 style={{ margin: 0, fontSize: '18px' }}>
                <i className="fas fa-user-plus me-2"></i>
                Asignar Parte a Personal
              </h5>
              <button 
                type="button" 
                onClick={() => setShowAsignarModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '5px'
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '20px' }}>
              <div style={{
                backgroundColor: '#d1ecf1',
                border: '1px solid #bee5eb',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                color: '#0c5460'
              }}>
                <i className="fas fa-info-circle me-2"></i>
                Selecciona el personal al que quieres asignar esta parte:
              </div>
              
              <div>
                {Array.isArray(personalList) && personalList.length > 0 ? (
                  personalList.map((usuario) => (
                    <button
                      key={usuario.id}
                      onClick={() => handleSeleccionarPersonal(usuario)}
                      style={{
                        width: '100%',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px',
                        marginBottom: '12px',
                        padding: '16px',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#f8f9fa';
                        e.target.style.borderColor = '#007bff';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'white';
                        e.target.style.borderColor = '#dee2e6';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{
                          backgroundColor: '#007bff',
                          color: 'white',
                          borderRadius: '50%',
                          width: '40px',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '12px'
                        }}>
                          <i className="fas fa-user"></i>
                        </div>
                        <div>
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                            {usuario.nombre}
                          </div>
                          <div style={{ color: '#6c757d', fontSize: '14px' }}>
                            {usuario.email}
                          </div>
                        </div>
                      </div>
                      <i className="fas fa-arrow-right" style={{ color: '#007bff' }}></i>
                    </button>
                  ))
                ) : (
                  <div style={{
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    borderRadius: '8px',
                    padding: '16px',
                    textAlign: 'center',
                    color: '#856404'
                  }}>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    No hay personal disponible para asignar.
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '20px',
              borderTop: '1px solid #dee2e6',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button 
                type="button" 
                onClick={() => setShowAsignarModal(false)}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                <i className="fas fa-times me-2"></i>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overlay para modal */}
      {showAsignarModal && (
        <div 
          className="modal-backdrop fade show"
          onClick={() => setShowAsignarModal(false)}
        ></div>
      )}

      {/* Modal de Confirmación */}
      {showConfirmacionModal && selectedUsuario && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
            width: '90%',
            maxWidth: '450px',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              backgroundColor: '#ffc107',
              color: '#212529',
              padding: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px'
            }}>
              <h5 style={{ margin: 0, fontSize: '18px' }}>
                <i className="fas fa-question-circle me-2"></i>
                Confirmar Asignación
              </h5>
              <button 
                type="button" 
                onClick={() => setShowConfirmacionModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#212529',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '5px'
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '30px 20px', textAlign: 'center' }}>
              <div style={{
                backgroundColor: '#ffc107',
                color: '#212529',
                borderRadius: '50%',
                width: '60px',
                height: '60px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px auto'
              }}>
                <i className="fas fa-user-plus" style={{ fontSize: '24px' }}></i>
              </div>
              
              <h6 style={{ marginBottom: '20px', fontSize: '16px' }}>
                ¿Estás seguro de que quieres asignar esta parte?
              </h6>
              
              <div style={{
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{
                    backgroundColor: '#007bff',
                    color: 'white',
                    borderRadius: '50%',
                    width: '50px',
                    height: '50px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '12px'
                  }}>
                    <i className="fas fa-user"></i>
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                      {selectedUsuario.nombre}
                    </div>
                    <div style={{ color: '#6c757d', fontSize: '14px' }}>
                      {selectedUsuario.email}
                    </div>
                  </div>
                </div>
                
                {/* Campo de fecha límite personalizada */}
                <div style={{ marginTop: '16px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontWeight: 'bold',
                    color: '#495057'
                  }}>
                    <i className="fas fa-calendar-alt me-2"></i>
                    Fecha límite para esta parte:
                  </label>
                  <input
                    type="date"
                    value={fechaLimitePersonalizada}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFechaLimitePersonalizada(val);
                      if (val) setHoraLimitePersonalizada(horaLimiteDefaultParaFecha(val));
                    }}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ced4da',
                      borderRadius: '6px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                  <label style={{ 
                    display: 'block', 
                    marginTop: '12px',
                    marginBottom: '8px', 
                    fontWeight: 'bold',
                    color: '#495057'
                  }}>
                    <i className="fas fa-clock me-2"></i>
                    Hora límite:
                  </label>
                  <input
                    type="time"
                    value={horaLimitePersonalizada}
                    onChange={(e) => setHoraLimitePersonalizada(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ced4da',
                      borderRadius: '6px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                    required
                  />
                  <small style={{ 
                    color: '#6c757d', 
                    fontSize: '12px',
                    marginTop: '4px',
                    display: 'block'
                  }}>
                    Fecha y hora límite de entrega (horario Lima). Sábado sugerido hasta 13:30.
                  </small>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '20px',
              borderTop: '1px solid #dee2e6',
              display: 'flex',
              justifyContent: 'center',
              gap: '10px'
            }}>
              <button 
                type="button" 
                onClick={() => setShowConfirmacionModal(false)}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                <i className="fas fa-times me-2"></i>
                Cancelar
              </button>
              <button 
                type="button" 
                onClick={handleConfirmarAsignacion}
                style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                <i className="fas fa-check me-2"></i>
                Confirmar Asignación
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default ExpedienteDetail;
